import {
	CompletePickOrderInput,
	PickOrderDetailFragment,
	RecordPickedQuantitiesInput,
} from '../../../graphql'

export type PickOrderLine = PickOrderDetailFragment['lines'][number]

/**
 * Oldest fulfillment order first.
 *
 * Order numbers are handed out in creation order, so sorting by the number is
 * sorting by age — and unlike a timestamp the UI never shows, the number is
 * printed on the row, so a picker can check the rule by eye.
 *
 * Both steps allocate by it: picking decides which order a short count fills,
 * packing decides which order's units get boxed first. One order ships whole
 * rather than two shipping incomplete, and it is the same order both times.
 */
export function byOldestFulfillmentOrder( a: PickOrderLine, b: PickOrderLine ): number {
	return Number( a.fulfillmentOrder.orderNumber ) - Number( b.fulfillmentOrder.orderNumber )
}

/**
 * A carton being built on the Pack step. `key` is a client-side identity for
 * React and for addressing the carton while it is edited; inventory-api numbers
 * the cartons and creates one Fulfillment per carton when the run completes.
 */
export type DraftCarton = {
	key: string,
	trackingNumber: string,
	/** pick order line id → quantity of that line in this carton. */
	contents: Record<string, number>,
}

/** pick order line id → quantity the picker actually found. */
export type PickedQuantities = Record<string, number>

/**
 * Lines a picker is still expected to work.
 *
 * A line whose fulfillment order was cancelled mid-run stays on the pick order
 * as the record of what the picker was told to pick, but inventory-api drops it
 * when the run completes. It is shown with its own treatment rather than
 * silently removed — a picker may already be holding those units.
 */
export function activeLines( lines: PickOrderLine[] ): PickOrderLine[] {
	return lines.filter( ( line ) => !line.isCancelled )
}

export function cancelledLines( lines: PickOrderLine[] ): PickOrderLine[] {
	return lines.filter( ( line ) => line.isCancelled )
}

export function initialPickedQuantities( lines: PickOrderLine[] ): PickedQuantities {
	return lines.reduce<PickedQuantities>( ( acc, line ) => {
		acc[ line.id ] = line.quantityPicked
		return acc
	}, {} )
}

export function pickedFor( picked: PickedQuantities, lineId: string ): number {
	return picked[ lineId ] ?? 0
}

/**
 * Lines where fewer were found than were called for. Short picks are a normal
 * outcome — surfaced so the picker confirms them deliberately, never treated as
 * a reason the order cannot complete.
 */
export function shortPickedLines( lines: PickOrderLine[], picked: PickedQuantities ): PickOrderLine[] {
	return activeLines( lines ).filter( ( line ) => pickedFor( picked, line.id ) < line.quantityToPick )
}

export function packedFor( cartons: DraftCarton[], lineId: string ): number {
	return cartons.reduce( ( sum, carton ) => sum + ( carton.contents[ lineId ] ?? 0 ), 0 )
}

/** How much of a line has been picked but not yet put in a carton. */
export function unpackedFor( picked: PickedQuantities, cartons: DraftCarton[], lineId: string ): number {
	return Math.max( pickedFor( picked, lineId ) - packedFor( cartons, lineId ), 0 )
}

export function totalUnpacked( lines: PickOrderLine[], picked: PickedQuantities, cartons: DraftCarton[] ): number {
	return activeLines( lines ).reduce( ( sum, line ) => sum + unpackedFor( picked, cartons, line.id ), 0 )
}

export function totalPicked( lines: PickOrderLine[], picked: PickedQuantities ): number {
	return activeLines( lines ).reduce( ( sum, line ) => sum + pickedFor( picked, line.id ), 0 )
}

export function totalToPick( lines: PickOrderLine[] ): number {
	return activeLines( lines ).reduce( ( sum, line ) => sum + line.quantityToPick, 0 )
}

/** Cartons with nothing in them are dropped rather than shipped empty. */
export function nonEmptyCartons( cartons: DraftCarton[] ): DraftCarton[] {
	return cartons.filter( ( carton ) => Object.values( carton.contents ).some( ( quantity ) => quantity > 0 ) )
}

/**
 * Takes every line whose picked count changed out of every carton, so the
 * packer repacks it against the new count. Trimming a changed line in place
 * would have to guess which carton the missing units came out of, and checked
 * per carton it misses a line split across several. Cartons this empties are
 * dropped; a carton that was already empty is the packer's and stays.
 */
export function unpackChangedLines(
	cartons: DraftCarton[],
	previous: PickedQuantities,
	next: PickedQuantities,
): { cartons: DraftCarton[], unpackedLineIds: string[] } {
	const lineIds = new Set( [ ...Object.keys( previous ), ...Object.keys( next ) ] )
	const unpackedLineIds = [ ...lineIds ].filter( ( lineId ) => (
		pickedFor( previous, lineId ) !== pickedFor( next, lineId ) && packedFor( cartons, lineId ) > 0
	) )
	if ( unpackedLineIds.length === 0 ) {
		return { cartons, unpackedLineIds }
	}
	const unpacked = new Set( unpackedLineIds )
	const nextCartons = cartons.flatMap( ( carton ) => {
		if ( !Object.keys( carton.contents ).some( ( lineId ) => unpacked.has( lineId ) ) ) {
			return [ carton ]
		}
		const contents = Object.fromEntries(
			Object.entries( carton.contents ).filter( ( [ lineId ] ) => !unpacked.has( lineId ) ),
		)
		const emptied = nonEmptyCartons( [ { ...carton, contents } ] ).length === 0
		return emptied ? [] : [ { ...carton, contents } ]
	} )
	return { cartons: nextCartons, unpackedLineIds }
}

/**
 * Absolute picked counts for every line still in play. Safe to send repeatedly
 * as the picker works.
 */
export function toRecordPickedInput(
	pickOrderId: string,
	lines: PickOrderLine[],
	picked: PickedQuantities,
): RecordPickedQuantitiesInput {
	return {
		pickOrderId,
		lines: activeLines( lines ).map( ( line ) => ( {
			pickOrderLineId: line.id,
			quantityPicked: pickedFor( picked, line.id ),
		} ) ),
	}
}

export function shortfallFor( line: PickOrderLine, picked: PickedQuantities ): number {
	return Math.max( line.quantityToPick - pickedFor( picked, line.id ), 0 )
}

/**
 * What becomes of the units a picker could not find.
 *
 * inventory-api completes a fulfillment order line once `quantity_fulfilled +
 * quantity_unfulfilled` reaches the quantity ordered, so the shortfall is not a
 * note about this run — it is the instruction that decides whether the order
 * ever gets the rest.
 *
 * - `short` reports the shortfall as `quantityUnfulfilled`. The line is fully
 *   accounted for and the fulfillment order completes without those units. They
 *   are not coming from this DC.
 * - `leaveOpen` reports only what was found. The fulfillment order keeps the
 *   remainder outstanding and can be picked again once stock is there.
 *
 * Leaving the line open is the default. Shorting closes out units the order is
 * still owed, so it happens only when the picker says so. A line skipped by
 * mistake must not quietly cancel the rest of the order.
 */
export type ShortfallDisposition = 'short' | 'leaveOpen'

/** pick order line id → what to do with that line's shortfall. */
export type ShortfallDispositions = Record<string, ShortfallDisposition>

export function dispositionFor(
	dispositions: ShortfallDispositions,
	lineId: string,
): ShortfallDisposition {
	return dispositions[ lineId ] ?? 'leaveOpen'
}

/**
 * Whether completing now would send inventory-api anything at all.
 *
 * A run that ships nothing and shorts nothing has no carton to report, and
 * inventory-api rejects a completion without one. That run has produced no
 * outcome to record, so cancelling it is the honest ending — the UI says so
 * rather than letting the button fail.
 */
export function hasReportableOutcome(
	lines: PickOrderLine[],
	picked: PickedQuantities,
	cartons: DraftCarton[],
	dispositions: ShortfallDispositions,
): boolean {
	if ( nonEmptyCartons( cartons ).length > 0 ) {
		return true
	}
	return activeLines( lines ).some( ( line ) => (
		shortfallFor( line, picked ) > 0 && dispositionFor( dispositions, line.id ) === 'short'
	) )
}

/** Short lines the picker has chosen to leave outstanding on their order. */
export function leftOpenLines(
	lines: PickOrderLine[],
	picked: PickedQuantities,
	dispositions: ShortfallDispositions,
): PickOrderLine[] {
	return shortPickedLines( lines, picked )
		.filter( ( line ) => dispositionFor( dispositions, line.id ) === 'leaveOpen' )
}

/**
 * What the run ships.
 *
 * Each carton line carries both halves of the outcome: `quantityFulfilled` is
 * what went in the box and `quantityUnfulfilled` is the shortfall, which
 * inventory-api records on the Fulfillment and uses to complete the fulfillment
 * order without adjusting recorded inventory.
 *
 * A shortfall is only reported for a line the picker chose to short. A line left
 * open reports what was found and nothing else, so inventory-api never accounts
 * for the missing units and the fulfillment order stays open for them.
 *
 * Two rules inventory-api enforces shape this:
 *
 * - A line that appears in no carton is never fulfilled at all, so a shorted
 *   line the picker found nothing for still has to be reported — with
 *   `quantityFulfilled` zero and the whole call-out as shortfall — or its
 *   fulfillment order would sit open forever. Those ride on the first carton.
 *   A line left open wants exactly that silence, so it is simply omitted.
 * - A carton line accounting for zero units is rejected, so a line that was
 *   picked in full and packed in full contributes no shortfall row.
 *
 * The shortfall is charged once per line, against the first carton holding it,
 * so a line split across cartons does not report it repeatedly.
 */
export function toCompleteInput(
	pickOrderId: string,
	lines: PickOrderLine[],
	picked: PickedQuantities,
	cartons: DraftCarton[],
	dispositions: ShortfallDispositions = {},
): CompletePickOrderInput {
	const packable = nonEmptyCartons( cartons )
	const shortfallCharged = new Set<string>()

	const shortfallToReport = ( line: PickOrderLine ): number => (
		dispositionFor( dispositions, line.id ) === 'short' ? shortfallFor( line, picked ) : 0
	)

	const packedCartons = packable.map( ( carton ) => ( {
		// A tracking number is never required to finish, so an untouched field
		// becomes null rather than an empty string.
		trackingNumber: carton.trackingNumber.trim() || null,
		lines: Object.entries( carton.contents )
			.filter( ( [ , quantity ] ) => quantity > 0 )
			.map( ( [ pickOrderLineId, quantity ] ) => {
				const line = lines.find( ( candidate ) => candidate.id === pickOrderLineId )
				const shortfall = line && !shortfallCharged.has( pickOrderLineId )
					? shortfallToReport( line )
					: 0
				shortfallCharged.add( pickOrderLineId )
				return {
					pickOrderLineId,
					quantityFulfilled: quantity,
					quantityUnfulfilled: shortfall,
				}
			} ),
	} ) )

	// Lines that never made it into a carton but are still owed to their
	// fulfillment order — the picker found none of them, or found some and packed
	// none. Reported as pure shortfall so the order completes. Only the ones being
	// shorted: a line left open has nothing to say and would be rejected as a
	// carton line accounting for zero units.
	const unreported = activeLines( lines )
		.filter( ( line ) => !shortfallCharged.has( line.id ) && shortfallToReport( line ) > 0 )
		.map( ( line ) => ( {
			pickOrderLineId: line.id,
			quantityFulfilled: 0,
			quantityUnfulfilled: shortfallToReport( line ),
		} ) )

	if ( unreported.length === 0 ) {
		return { pickOrderId, cartons: packedCartons }
	}

	// With nothing packed at all there is no carton to hang them on, so the run
	// reports one carton holding only shortfall.
	if ( packedCartons.length === 0 ) {
		return { pickOrderId, cartons: [ { trackingNumber: null, lines: unreported } ] }
	}

	return {
		pickOrderId,
		cartons: packedCartons.map( ( carton, index ) => (
			index === 0 ? { ...carton, lines: [ ...carton.lines, ...unreported ] } : carton
		) ),
	}
}
